import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sun, Moon, Menu, X, PawPrint, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", key: "nav.home" },
  { to: "/#how", key: "nav.how" },
  { to: "/#services", key: "nav.services" },
  { to: "/#families", key: "nav.families" },
  { to: "/#about-owner", key: "nav.about" },
  { to: "/#testimonials", key: "nav.testimonials" },
  { to: "/#faq", key: "nav.faq" },
];

export default function Header() {
  const { theme, toggle } = useTheme();
  const { lang, setLang, t } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();

  const dashboardPath = user && user.role ? `/${user.role}` : "/login";

  const handleNav = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (loc.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header className="sticky top-0 z-50 glass" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center" data-testid="brand-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_pawsitive-app-1/artifacts/54jk48in_ChatGPT%20Image%20Apr%2025%2C%202026%2C%2004_29_53%20PM.png"
            alt="Oh My Dog! Training with Heart. Results that Last."
            className="h-14 sm:h-16 w-auto wiggle"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <button
              key={n.to}
              onClick={() => handleNav(n.to)}
              className="px-3 py-2 text-sm font-semibold rounded-full hover:bg-primary/10 hover:text-primary transition"
              data-testid={`nav-${n.key.replace(/\./g, "-")}`}
            >
              {t(n.key)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setLang(lang === "en" ? "es" : "en")}
            className="hidden sm:flex items-center gap-1 px-3 py-2 rounded-full text-xs font-bold uppercase tracking-wider hover:bg-primary/10"
            data-testid="lang-toggle"
            aria-label="toggle language"
          >
            <Globe className="w-4 h-4" />
            {lang === "en" ? "EN" : "ES"}
          </button>
          <button
            onClick={toggle}
            className="p-2 rounded-full hover:bg-primary/10 transition"
            data-testid="theme-toggle"
            aria-label="toggle theme"
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>

          {user ? (
            <>
              <Button
                variant="ghost"
                className="hidden md:inline-flex rounded-full font-semibold"
                onClick={() => navigate(dashboardPath)}
                data-testid="header-dashboard-btn"
              >
                {t("nav.dashboard")}
              </Button>
              <Button
                variant="outline"
                className="hidden md:inline-flex rounded-full font-semibold"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                data-testid="header-logout-btn"
              >
                {t("nav.logout")}
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              className="hidden md:inline-flex rounded-full font-semibold"
              onClick={() => navigate("/login")}
              data-testid="header-login-btn"
            >
              {t("nav.login")}
            </Button>
          )}

          <Button
            className="rounded-full font-semibold shadow-md hidden sm:inline-flex"
            onClick={() => navigate("/request-training")}
            data-testid="header-cta-btn"
          >
            {t("nav.contact")}
          </Button>

          <button
            className="lg:hidden p-2 rounded-full hover:bg-primary/10"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
            data-testid="header-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border/50 bg-background/95 backdrop-blur" data-testid="mobile-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.to}
                onClick={() => handleNav(n.to)}
                className="text-left px-3 py-2 rounded-lg hover:bg-primary/10 font-semibold"
              >
                {t(n.key)}
              </button>
            ))}
            <div className="flex gap-2 pt-2">
              {user ? (
                <>
                  <Button
                    className="flex-1 rounded-full"
                    onClick={() => {
                      setOpen(false);
                      navigate(dashboardPath);
                    }}
                  >
                    {t("nav.dashboard")}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 rounded-full"
                    onClick={() => {
                      logout();
                      setOpen(false);
                      navigate("/");
                    }}
                  >
                    {t("nav.logout")}
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => {
                    setOpen(false);
                    navigate("/login");
                  }}
                >
                  {t("nav.login")}
                </Button>
              )}
              <Button
                className="flex-1 rounded-full"
                onClick={() => {
                  setOpen(false);
                  navigate("/request-training");
                }}
              >
                {t("nav.contact")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
